import type {
  FormStorageLocProductSale,
  PurchaseRequestFlowState,
} from "@fero/pages";
import type { SelectedProduct } from "@fero/stores";
import type { NominatedPurchaserIndexItemView } from "adl-gen/ferovinum/app/api";
import { assertNotUndefined } from "utils/hx/util/types";
import { limitPrecision } from "utils/numeric-utils";
import type { SelectedProductsFormValues } from "../new-organisation-purchase-request-setup-page";
import type { SetupPurchaserRequestFormValues } from "../organisation-purchase-request-setup-page";

export function getInitialValuesForProductsForm(
  initialValues?: PurchaseRequestFlowState["setup"],
): SetupPurchaserRequestFormValues {
  if (!initialValues) {
    return {
      products: [],
      dealDiscountAmount: null,
      bondedSale: true,
      salePriceType: "exDutyAndVat",
    };
  }

  return {
    products:
      initialValues.products.map(
        (p) =>
          ({
            product: p.product,
            totalAvailable: p.totalAvailable,
            totalProducible: p.totalProducible,
            unitPrice: assertNotUndefined(p.unitPrice.toString()),
            priceDiscount: p.priceDiscount?.toString(),
            paidUnits: assertNotUndefined(p.paidUnits.toString()),
            freeUnits: p.freeUnits?.toString(),
          }) as FormStorageLocProductSale,
      ) ?? [],
    dealDiscountAmount: initialValues.dealDiscountAmount,
    bondedSale: initialValues.bondedSale ?? true,
    salePriceType: initialValues.salePriceType ?? "exDutyAndVat",
    poFile: initialValues.poFile,
    purchaser: initialValues.purchaser,
  };
}

export function getNewInitialValuesForProductsForm(
  initialValues?: PurchaseRequestFlowState<NominatedPurchaserIndexItemView>["setup"],
): SelectedProductsFormValues {
  if (!initialValues) {
    return {
      selectedProducts: [],
      dealDiscountAmount: "0",
      dealDiscountPercentage: "0",
      bondedSale: true,
      salePriceType: "exDutyAndVat",
    };
  }

  const selectedProducts =
    initialValues.products.map(
      (p) =>
        ({
          productId: p.product.id,
          summary: p.product,
          salePrice: Number(p.unitPrice),
          quantity: Number(p.paidUnits),
          available: p.totalAvailable,
          producible: p.totalProducible,
          subtotal: Number(p.unitPrice) * Number(p.paidUnits),
          lineItemDiscount: p.lineItemDiscount ?? 0,
          freeUnits: p.freeUnits?.valueOf() ?? 0,
          attributeSelection: p.attributeSelection,
        }) as SelectedProduct,
    ) ?? [];

  // Net subtotal should account for the discount taken from each line item
  const netSubtotal = selectedProducts.reduce((acc, curr) => {
    const lineItemDiscountAmount =
      (curr.lineItemDiscount / 100) * curr.subtotal;
    return acc + curr.subtotal - lineItemDiscountAmount;
  }, 0);
  // Deal discount percentage should be the discount amount divided by the net subtotal
  const dealDiscountPercentage =
    initialValues.dealDiscountAmount && netSubtotal > 0
      ? limitPrecision(
          (Number(initialValues.dealDiscountAmount) / netSubtotal) * 100,
          2,
        )
      : 0;
  return {
    selectedProducts: selectedProducts,
    dealDiscountAmount: initialValues.dealDiscountAmount ?? "0",
    dealDiscountPercentage: dealDiscountPercentage.toString(),
    bondedSale: initialValues.bondedSale ?? true,
    salePriceType: initialValues.salePriceType ?? "exDutyAndVat",
    poFile: initialValues.poFile,
  };
}
